import { useCallback, useMemo, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import type {
  BidDirection,
  BidLevelActivationGroup,
  BidLevelInput,
  DragPayload,
  VolumeCapacityConfig,
} from '@/features/bidding/types/bidLadderInput'
import {
  createDefaultVolumeCapacityConfig,
  DEFAULT_PRICES_MFRR,
  MAX_BID_LEVELS_MFRR,
} from '@/features/bidding/utils/bidLadderInput/constants'
import { getDirectionFromMarketProgram } from '@/features/bidding/utils/bidLadderInput/getDirectionFromMarketProgram'
import { wattHoursToMegawattHours } from '@/utils/powerEnergyTransformations'

function createInitialLevels(
  maxBidLevels: number,
  defaultPrices: number[],
  withVolumeCapacity = false,
): BidLevelInput[] {
  return Array.from({ length: maxBidLevels }, (_, index) => ({
    id: uuidv4(),
    upPrice: defaultPrices[index],
    downPrice: defaultPrices[index],
    assignedGroups: [],
    ...(withVolumeCapacity && {
      upVolumeCapacity: createDefaultVolumeCapacityConfig(),
      downVolumeCapacity: createDefaultVolumeCapacityConfig(),
    }),
  }))
}

export type UseBidLadderInputStateReturn = {
  levels: BidLevelInput[]
  updatePrice: (levelId: string, direction: BidDirection, price: number | null) => void
  assignGroups: (levelId: string, payload: DragPayload) => void
  /** Removes all activation groups from the same regulation object (both UP and DOWN) */
  unassignRegulationObjectGroup: (levelId: string, regulationObjectCode: string) => void
  getTotalMW: (levelId: string, direction: BidDirection) => number
  /** Returns total MW across ALL levels for BOTH directions combined (for relative bar width) */
  getTotalMWAcrossAllLevels: () => number
  /** Memoized set of all assigned group UUIDs */
  assignedGroupUuids: Set<string>
  /** Resets all levels to initial state, removing all assigned groups */
  resetLevels: () => void
  /** Updates volume capacity configuration for a level and direction */
  updateVolumeCapacity: (levelId: string, direction: BidDirection, config: VolumeCapacityConfig) => void
}

type UseBidLadderInputStateOptions = {
  /** Default prices per level */
  defaultPrices?: number[]
  /** Maximum number of bid levels (defaults to MFRR = 3) */
  maxBidLevels?: number
  /** Whether to initialize volume capacity configuration (for FCR markets without activation groups) */
  withVolumeCapacity?: boolean
}

export function useBidLadderInputState(options: UseBidLadderInputStateOptions = {}): UseBidLadderInputStateReturn {
  const {
    defaultPrices = DEFAULT_PRICES_MFRR,
    maxBidLevels = MAX_BID_LEVELS_MFRR,
    withVolumeCapacity = false,
  } = options
  const [levels, setLevels] = useState<BidLevelInput[]>(() =>
    createInitialLevels(maxBidLevels, defaultPrices, withVolumeCapacity),
  )

  const updatePrice = useCallback((levelId: string, direction: BidDirection, price: number | null) => {
    setLevels((prev) =>
      prev.map((level) => {
        if (level.id !== levelId) return level
        return {
          ...level,
          ...(direction === 'up' ? { upPrice: price } : { downPrice: price }),
        }
      }),
    )
  }, [])

  const assignGroups = useCallback((levelId: string, payload: DragPayload) => {
    setLevels((prev) => {
      // If moving from another level, handle as a move operation
      if (payload.sourceLevelId) {
        // Don't allow dropping on the same level
        if (payload.sourceLevelId === levelId) return prev

        // Find the source level and get the groups to move (by regulation object code)
        const sourceLevel = prev.find((l) => l.id === payload.sourceLevelId)
        if (!sourceLevel) return prev

        const regulationObjectCodes = new Set(payload.regulationObjectCodes)
        const groupsToMove = sourceLevel.assignedGroups.filter((g) => regulationObjectCodes.has(g.regulationObjectCode))

        if (groupsToMove.length === 0) return prev

        return prev.map((level) => {
          if (level.id === payload.sourceLevelId) {
            // Remove groups from source level
            return {
              ...level,
              assignedGroups: level.assignedGroups.filter((g) => !regulationObjectCodes.has(g.regulationObjectCode)),
            }
          }
          if (level.id === levelId) {
            // Add groups to target level
            return {
              ...level,
              assignedGroups: [...level.assignedGroups, ...groupsToMove],
            }
          }
          return level
        })
      }

      // New groups from sidebar - convert and add
      const alreadyAssigned = new Set<string>()
      prev.forEach((level) => {
        level.assignedGroups.forEach((group) => {
          alreadyAssigned.add(group.uuid)
        })
      })

      const newGroups: BidLevelActivationGroup[] = payload.activationGroups
        .filter((ag) => !alreadyAssigned.has(ag.uuid))
        .map((ag) => ({
          uuid: ag.uuid,
          code: ag.code,
          regulationObjectCode: ag.regulationObjectCode,
          capacityMW: wattHoursToMegawattHours(ag.capacityMW),
          direction: getDirectionFromMarketProgram(ag.marketProgram),
        }))

      if (newGroups.length === 0) return prev

      return prev.map((level) => {
        if (level.id !== levelId) return level
        return {
          ...level,
          assignedGroups: [...level.assignedGroups, ...newGroups],
        }
      })
    })
  }, [])

  const unassignRegulationObjectGroup = useCallback((levelId: string, regulationObjectCode: string) => {
    setLevels((prev) =>
      prev.map((level) => {
        if (level.id !== levelId) return level
        // Remove ALL activation groups from the same regulation object (both UP and DOWN)
        return {
          ...level,
          assignedGroups: level.assignedGroups.filter((g) => g.regulationObjectCode !== regulationObjectCode),
        }
      }),
    )
  }, [])

  const getTotalMW = useCallback(
    (levelId: string, direction: BidDirection): number => {
      const level = levels.find((l) => l.id === levelId)
      if (!level) return 0
      const fromGroups = level.assignedGroups
        .filter((g) => g.direction === direction)
        .reduce((sum, g) => sum + g.capacityMW, 0)
      if (fromGroups > 0) return fromGroups
      const config = direction === 'up' ? level.upVolumeCapacity : level.downVolumeCapacity
      if (config?.mode === 'fixed' && config.value != null) return config.value
      return 0
    },
    [levels],
  )

  const getTotalMWAcrossAllLevels = useCallback((): number => {
    return levels.reduce((total, level) => {
      const levelTotal = level.assignedGroups.reduce((sum, g) => sum + g.capacityMW, 0)
      return total + levelTotal
    }, 0)
  }, [levels])

  const assignedGroupUuids = useMemo(() => {
    const uuids = new Set<string>()
    levels.forEach((level) => {
      level.assignedGroups.forEach((g) => uuids.add(g.uuid))
    })
    return uuids
  }, [levels])

  const resetLevels = useCallback(() => {
    setLevels(createInitialLevels(maxBidLevels, defaultPrices, withVolumeCapacity))
  }, [defaultPrices, maxBidLevels, withVolumeCapacity])

  const updateVolumeCapacity = useCallback((levelId: string, direction: BidDirection, config: VolumeCapacityConfig) => {
    setLevels((prev) =>
      prev.map((level) => {
        if (level.id !== levelId) return level
        return {
          ...level,
          ...(direction === 'up' ? { upVolumeCapacity: config } : { downVolumeCapacity: config }),
        }
      }),
    )
  }, [])

  return useMemo(
    () => ({
      levels,
      updatePrice,
      assignGroups,
      unassignRegulationObjectGroup,
      getTotalMW,
      getTotalMWAcrossAllLevels,
      assignedGroupUuids,
      resetLevels,
      updateVolumeCapacity,
    }),
    [
      levels,
      updatePrice,
      assignGroups,
      unassignRegulationObjectGroup,
      getTotalMW,
      getTotalMWAcrossAllLevels,
      assignedGroupUuids,
      resetLevels,
      updateVolumeCapacity,
    ],
  )
}
